import { Component, ViewChild } from '@angular/core';
import { AuthenticationService } from './core/services/authentication.service';
import { Store } from '@ngxs/store';
import { UsersState } from './core/states/users/users.state';
import { PartnerDTO, RoleDTO, UserDTO } from '@lkp/parking-sdk';
import { environment } from '../environments/environment';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, delay, filter, takeUntil } from 'rxjs';
import { DrawerServiceService } from './core/services/drawer-service.service';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { DriverSearchDialogComponent } from './components/driver-search-dialog/driver-search-dialog.component';
import { SetCurrentPartner } from './core/states/partner/partner.action';
import { SetLoggedInUser } from './core/states/users/user.actions';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  title = 'administration';
  description: string = '';
  version: string = '';
  isProd = true;
  isAuthenticated = false;
  drawerActive: boolean = false;
  user?: UserDTO;
  isAdmin: boolean = false;
  isDriverMode: boolean = false;
  partner?: PartnerDTO;
  hideLayout: boolean = false;
  drawerContent?: string;

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly authenticationService: AuthenticationService,
    readonly store: Store,
    readonly router: Router,
    readonly activatedRoute: ActivatedRoute,
    readonly drawerService: DrawerServiceService,
    private readonly dialog: MatDialog
  ) {
    // Get hideLayout parameter from route
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((event) => {
        this.hideLayout =
          this.activatedRoute.snapshot.firstChild?.data['hideLayout'];
      });
  }

  // Access to the side drawer for opening in normal mode
  @ViewChild('drawer') drawer!: MatDrawer;

  logout() {
    this.authenticationService.logout();
    this.isAuthenticated = !!this.authenticationService.isAuthenticated();
  }

  openDriverSearch() {
    if (this.isDriverMode) {
      this.dialog.open(DriverSearchDialogComponent, {
        width: '640px',
        autoFocus: true,
      });
    } else {
      this.drawerService.changeContent('search');
      this.drawer.open();
    }
  }

  openRegisterDrawer() {
    if (!this.isDriverMode) {
      this.drawerService.changeContent('register');
      this.drawer.open();
    }
  }

  closeDrawer() {
    this.drawerActive = false;
  }

  ngOnInit() {
    this.description = environment.description;
    this.isProd = environment.isProd;
    this.version = environment.version;
    // Seed initial auth and driver mode from storage so UI does not flash normal mode on reload
    this.isAuthenticated = !!this.authenticationService.isAuthenticated();
    try {
      const persisted = sessionStorage.getItem('driver-mode');
      if (this.isAuthenticated && persisted === 'true') {
        this.isDriverMode = true;
      }
    } catch {}
    this.store
      .select(UsersState.driverMode)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((driverMode) => {
        this.isDriverMode = driverMode;
      });

    this.authenticationService.authenticationState
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.isAuthenticated = v;

        if (this.isAuthenticated) {
          // Ensure logged-in user is loaded after reload so driverMode selector can evaluate
          this.store.dispatch(new SetLoggedInUser());
          this.store
            .select(UsersState.loggedInUser)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((v) => {
              this.user = v;
              this.isAdmin = v?.roles
                ? v.roles.some((role: RoleDTO) => role.name === 'ROLE_ADMIN')
                : false;

              this.partner = v?.roles
                ? v.roles.find((role: RoleDTO) => role.name === 'ROLE_PARTNER')
                    ?.partner
                : undefined;

              if (this.partner) {
                this.setCurrentPartner(this.partner);
              }
            });

          this.drawerService.currentContent
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((content) => {
              this.drawerContent = content;
            });
        } else {
          this.user = undefined;
          this.isAdmin = false;
          this.partner = undefined;
          this.isDriverMode = false;
          this.drawerContent = undefined;
        }
      });
  }

  setCurrentPartner(partner: PartnerDTO) {
    partner &&
      this.store
        .dispatch(new SetCurrentPartner(partner))
        .pipe(takeUntil(this.onDestroy$), delay(0))
        .subscribe(() => {
          this.router.navigate(['/partnerportalen/' + partner.partnerId]);
        });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
